import React, { useCallback, useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { toast } from 'sonner';
import { API, axios } from '../../utils/api';
import Icona from '../cliente/Icone';
import { errore, quandoBreve } from './comuni';

// Impostazioni → Cestino (solo admin): immobili e clienti eliminati, con chi e quando.
// "Ripristina" rimette tutto com'era (visite, proposte, documenti, cronologia).
//
// Le due righe si comportano in modo diverso, di proposito (backend/cestino.py):
// - un IMMOBILE resta qui **per sempre**, finché un admin non premette «Elimina per sempre»: una casa cancellata
//   per sbaglio era recuperabile 30 giorni e poi sparivano anche le foto dell'annuncio, che non si riprendono più;
// - un CLIENTE scade dopo 30 giorni, ed è voluto: documenti d'identità, buste paga, permessi di soggiorno, foto
//   delle visite ed export sono dati personali, e tenerli per sempre «per comodità» sarebbe la scelta sbagliata.

const conta = (n, una, tante) => `${n} ${n === 1 ? una : tante}`;

export const riassuntoCestino = (v) => {
  const c = v.conteggi || {};
  const pezzi = v.tipo === 'cliente'
    ? [conta(c.immobili || 0, 'immobile', 'immobili'), conta(c.visite || 0, 'visita', 'visite'), conta(c.documenti || 0, 'documento', 'documenti')]
    : [conta(c.visite || 0, 'visita', 'visite'), conta(c.proposte || 0, 'proposta', 'proposte')];
  return pezzi.join(', ');
};

export const linkRipristinato = (r) => (r.tipo === 'cliente' ? `/admin/clienti/${r.cliente_id}` : `/admin/clienti/${r.cliente_id}?evidenzia=${r.oggetto_id}#immobili`);

const Cestino = () => {
  const [dati, setDati] = useState(null);
  const [attesa, setAttesa] = useState(null);
  const [fatti, setFatti] = useState([]);
  const [daEliminare, setDaEliminare] = useState(null);   // la voce per cui si sta chiedendo conferma
  const [parola, setParola] = useState('');
  const carica = useCallback(() => {
    axios.get(`${API}/admin/cestino`).then((r) => setDati(r.data)).catch(() => setDati({ voci: [], giorni: 30 }));
  }, []);
  useEffect(() => { carica(); }, [carica]);
  const ripristina = async (v) => {
    setAttesa(v.id);
    try {
      const r = await axios.post(`${API}/admin/cestino/${v.id}/ripristina`);
      toast.success(`${v.nome}: ripristinato`);
      setFatti((f) => [{ ...r.data, nome: v.nome }, ...f]);
      carica();
    } catch (e) {
      toast.error(errore(e, 'Non ripristinato'));
    } finally { setAttesa(null); }
  };
  // «Elimina per sempre»: è l'unico momento in cui le foto di un annuncio lasciano R2, e non si torna indietro.
  // Per questo serve scrivere ELIMINA, come per l'eliminazione di un cliente.
  const perSempre = async () => {
    const v = daEliminare;
    setAttesa(v.id);
    try {
      await axios.delete(`${API}/admin/cestino/${v.id}?conferma=ELIMINA`);
      toast.success(`${v.nome}: eliminato per sempre`);
      setDaEliminare(null);
      setParola('');
      carica();
    } catch (e) {
      toast.error(errore(e, 'Non eliminato'));
    } finally { setAttesa(null); }
  };
  return (
    <section className="card" data-testid="cestino">
      <div className="ttl"><div className="i r"><Icona nome="cestino" /></div><h2>Cestino {dati ? <small>{dati.voci.length}</small> : null}</h2></div>
      <p className="imp-nota" style={{ marginBottom: '.8rem' }}>
        Gli <b>immobili</b> restano qui finché non li eliminate per sempre: le foto dell&apos;annuncio non si
        riprendono più da nessuna parte. I <b>clienti</b> si cancellano da soli dopo {dati?.giorni || 30} giorni,
        documenti e foto compresi.
      </p>
      {fatti.map((r) => (
        <p key={r.oggetto_id} className="imp-nota" style={{ color: 'var(--ok)', marginBottom: '.5rem' }}>{r.nome} è tornato al suo posto. <Link className="link" to={linkRipristinato(r)}>Apri →</Link></p>
      ))}
      {!dati && <p className="vuoto">Carico il cestino…</p>}
      {dati && !dati.voci.length && <p className="vuoto">Il cestino è vuoto.</p>}
      {daEliminare && (
        <div className="imp-conferma" data-testid="cestino-conferma" role="alertdialog" aria-label="Elimina per sempre"
          style={{ border: '1px solid rgba(217,122,108,.45)', background: 'rgba(217,122,108,.07)', borderRadius: 12, padding: '.8rem', marginBottom: '.8rem' }}>
          <p style={{ marginBottom: '.5rem' }}>
            <b>{daEliminare.nome}</b> viene eliminato per sempre, con le sue foto{daEliminare.tipo === 'cliente' ? ', i suoi documenti e i suoi prospetti mutuo' : ''}.
            Non si torna indietro. Scrivete <b>ELIMINA</b> per confermare.
          </p>
          <input aria-label="Conferma con ELIMINA" value={parola} onChange={(e) => setParola(e.target.value)} placeholder="ELIMINA" />
          <div style={{ marginTop: '.5rem', display: 'flex', gap: '.4rem' }}>
            <button type="button" className="btn sm" disabled={parola.trim() !== 'ELIMINA' || attesa === daEliminare.id}
              onClick={perSempre}>Elimina per sempre</button>
            <button type="button" className="btn sm p" onClick={() => { setDaEliminare(null); setParola(''); }}>Annulla</button>
          </div>
        </div>
      )}
      {dati && dati.voci.length > 0 && (
        <div className="tabella-box">
          <table className="tabella">
            <thead><tr><th>Cosa</th><th>Con</th><th>Eliminato</th><th>Resta</th><th /></tr></thead>
            <tbody>
              {dati.voci.map((v) => (
                <tr key={v.id} data-testid={`cestino-${v.id}`}>
                  <td><span className={`tag ${v.tipo === 'cliente' ? 't-v' : 't-b'}`} style={{ marginRight: '.4rem' }}>{v.tipo === 'cliente' ? 'Cliente' : 'Immobile'}</span>{v.nome}
                    {v.tipo === 'immobile' && v.cliente_nome && <small>di {v.cliente_nome}</small>}</td>
                  <td className="muto">{riassuntoCestino(v)}</td>
                  <td className="nowrap">{quandoBreve(v.eliminato_il)}<small>{v.eliminato_da?.nome || '—'}</small></td>
                  <td className={`nowrap${v.scade && v.giorni_rimasti <= 3 ? ' bad' : ''}`}>
                    {v.scade ? conta(v.giorni_rimasti ?? 0, 'giorno', 'giorni') : <span className="muto">senza scadenza</span>}
                  </td>
                  <td className="dx">
                    <button type="button" className="btn xs" disabled={attesa === v.id} onClick={() => ripristina(v)}>Ripristina</button>
                    <button type="button" className="btn xs" style={{ marginLeft: '.35rem' }} disabled={attesa === v.id}
                      onClick={() => { setDaEliminare(v); setParola(''); }}>Elimina per sempre</button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </section>
  );
};

export default Cestino;
